import { signedFetch } from '../../super-picker/services/api';

const BASE = '/api/super-calculator/example-history';

export interface ExampleHistoryApiEntry {
  id: number;
  name: string;
  remarks?: string;
  createdAt: string | null;
  updatedAt: string | null;
  profiles: unknown;
  propertyType?: unknown;
  purchasePlannerState?: unknown;
}

export interface CreateExampleHistoryRequest {
  name: string;
  profiles: unknown[];
  propertyType?: unknown;
  purchasePlannerState?: unknown;
  remarks?: string;
}

export interface PatchExampleHistoryRequest {
  name?: string;
  remarks?: string;
  profiles?: unknown[];
  propertyType?: unknown;
  purchasePlannerState?: unknown;
}

/** Carries the HTTP status so a failed save can say *why* it failed
 *  (400 = name too long / bad payload, 401 = session, 500 = server/DB). */
export class ExampleHistoryApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ExampleHistoryApiError';
    this.status = status;
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await signedFetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = typeof errorData?.error === 'string' ? errorData.error : 'Request failed';
    throw new ExampleHistoryApiError(message, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const listExampleHistory = async (): Promise<ExampleHistoryApiEntry[]> => {
  const data = await requestJson<{ entries: ExampleHistoryApiEntry[] }>(BASE, { method: 'GET' });
  return data.entries ?? [];
};

export const createExampleHistory = async (body: CreateExampleHistoryRequest): Promise<ExampleHistoryApiEntry> =>
  requestJson<ExampleHistoryApiEntry>(BASE, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const patchExampleHistory = async (
  id: number,
  body: PatchExampleHistoryRequest
): Promise<ExampleHistoryApiEntry> =>
  requestJson<ExampleHistoryApiEntry>(`${BASE}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteExampleHistory = async (id: number): Promise<void> => {
  await requestJson<{ success: boolean }>(`${BASE}/${id}`, { method: 'DELETE' });
};

/** Maps UI history key `history-<id>` to numeric server id when possible. */
export const parseHistoryNumericId = (historyId: string): number | null => {
  const m = /^history-(\d+)$/.exec(historyId);
  if (!m) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};
